import React, { useState, useMemo } from 'react'
import { Box, Button, Divider } from '@mui/material'
import { DrawerContentProps } from './AddConsumerPanel'
import SearchBar from '@/components/common/SearchBar'
import DataGridPremiumDemo from '@/components/Table/DataGrid'
import columnsConfig from '@/components/features/config/columns/consumers/column.AnalyticsConsumerTable.config'
import MapHeaderIcon from '@/components/heder/MapHeaderIcon'
import filterhHorizontal from '@assets/Analytics/filterhHorizontal.svg'
import { useAppTheme } from '@/theme/ThemeContext'

const AnalyticsDrawerContent: React.FC<DrawerContentProps> = ({ onClose, rows = [] }) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const columns = columnsConfig
  const [searchValue, setSearchValue] = useState('')
  
  const filteredRows = useMemo(() => {
    if (!searchValue.trim()) return rows
    const q = searchValue.toLowerCase().trim()
    return rows.filter(r => 
      String(r.name ?? '').toLowerCase().includes(q) ||
      String(r.meterNumber ?? r.meter_number ?? '').toLowerCase().includes(q) ||
      String(r.waterCard ?? r.billing_id ?? '').toLowerCase().includes(q) ||
      String(r.address ?? '').toLowerCase().includes(q) ||
      String(r.payerNumber ?? '').toLowerCase().includes(q)
    )
  }, [rows, searchValue])

  const iconConfigs = [
    {
      icon: filterhHorizontal,
      alt: 'filter',
      tooltip: 'פלטור',
      action: 'filter',
    },
  ]

  const handleAdd = () => {
    onClose()
  }

  const handleClear = () => {
    setSearchValue('')
  }
  const handleIconClick = (_: string) => {}

  return (
    <Box sx={{ direction: 'rtl', px: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1.5,
          gap: 2
        }}
      >
        <Box
          sx={{ display: 'flex', flexDirection: 'row', gap: 1.5, flex: 1 }}
        >
          <SearchBar
            width="100%"
            height={38}
            onSearch={setSearchValue}
            executeSearch={setSearchValue}
            placeholder="הקלד מספר מד, כרטיס מים או כתובת"
            sx={{
                bgcolor: isDarkMode ? '#091327 !important' : '#FFFFFF !important',
                border: isDarkMode ? '1.5px solid #43597F' : '1.2px solid #EAEDF2',
                '& .MuiInputBase-root': {
                    fontSize: '13px',
                    direction: 'rtl',
                    color: isDarkMode ? '#FFFFFF' : '#003F61',
                },
                '& > span, & > div:first-of-type': { 
                    color: isDarkMode ? '#FFFFFF' : '#003F61',
                }
            }}
          />
          {iconConfigs.map((config, index) => (
            <MapHeaderIcon
              key={index}
              icon={config.icon}
              alt={config.alt}
              onClick={() => handleIconClick(config.action)}
              sx={{ bgcolor: isDarkMode ? '#202B40' : '#FFFFFF', border: isDarkMode ? '1.5px solid #43597F' : '1px solid #E6ECF2' }}
            />
          ))}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="text"
            onClick={handleClear}
            disableRipple
            sx={{
              fontSize: 14,
              fontWeight: 900, // Ultra-Bold
              color: isDarkMode ? '#94A3B8' : '#64748B',
              px: 1,
              minWidth: 'auto',
              backgroundColor: 'transparent',
              fontFamily: 'Rubik',
              '&:hover': { color: '#BBD7FF', backgroundColor: 'transparent' },
            }}
          >
            נקה הכל
          </Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            sx={{
              fontSize: 14,
              fontWeight: 900, // Ultra-Bold
              bgcolor: '#004A7C', // More contrast
              border: 'none',
              color: '#FFFFFF',
              px: 3,
              fontFamily: 'Rubik',
              '&:hover': {
                bgcolor: '#003559',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              },
              borderRadius: '6px',
            }}
          >
            הוספה
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 1.5, borderColor: isDarkMode ? 'rgba(255,255,255,0.06)' : '#E6ECF2' }} />
      
      <Box sx={{ 
          height: 'calc(100vh - 180px)', 
          border: isDarkMode ? '1.5px solid #43597F' : '1px solid #EAEDF2',
          borderRadius: 0,
          overflow: 'hidden',
          bgcolor: isDarkMode ? '#1D263B' : '#FFFFFF'
      }}>
        <DataGridPremiumDemo
            checkboxSelection
            columns={columns}
            rows={filteredRows}
            hideFooter={true}
            headerPosition="bottom"
            pageSize={25}
            rowHeight={40}
        />
      </Box>
    </Box>
  )
}

export default AnalyticsDrawerContent
